import React, { useState } from "react";
import { toast } from "../../lib/toast";
import { useExcelData } from "../../../context/ExcelDataContext"; // IMPORT CONTEXT
import { useSimplAI } from "../../../imports/useSimpleAI"; // IMPORT HOOK
import { groupCubeRows, usd, type ExecutiveTransaction } from "../../data/executiveOverview";

const productColors = ["#3265AA", "#51A2FF", "#AD5E9D", "#776EA9", "#4D80AF", "#FF70BF", "#A297B4", "#A9264F", "#C9C0D5", "#61BA93"];

// HARDCODED: Column mapping (original name -> renamed name)
const COST_MODEL_COLUMN_MAPPING = {
  "Input_1": "Line_Item_Description",
  "Input_11": "Supplier_Region",
  "Input_29": "Qty"
};

export function SpendByProductPanel({ rows }: { rows: ExecutiveTransaction[] }) {
  
  const { excelData, setAnalysisResult: setContextAnalysisResult, navigate } = useExcelData(); // GET EXCEL DATA FROM CONTEXT & CONTEXT RESULT SETTER
  const { runCostModelAnalysis, loading } = useSimplAI(); // GET COST MODEL ANALYSIS FUNCTION
  
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showResult, setShowResult] = useState(false);

  /* ================= EXTRACT HARDCODED 3 COLUMNS FROM EXCEL DATA ================= */

  const extractThreeColumns = (data: Record<string, any>[]) => {
    if (!data || data.length === 0) {
      console.warn("⚠️ No excel data available");
      return null;
    }

    // GET ALL COLUMN NAMES
    const allColumns = Object.keys(data[0]);
    console.log("📋 Available columns:", allColumns);

    // USE HARDCODED COLUMNS
    const originalColumns = Object.keys(COST_MODEL_COLUMN_MAPPING);
    console.log("Selected columns for extraction:", originalColumns);
    console.log("Column mapping:", COST_MODEL_COLUMN_MAPPING);

    // EXTRACT ONLY THESE HARDCODED COLUMNS FROM ALL ROWS
    const extractedData = data.map((row) => {
      const newRow: Record<string, any> = {};
      
      originalColumns.forEach((col) => {
        // Check if column exists, if not set to empty string
        newRow[col] = row[col] !== undefined ? row[col] : "";
      });
      
      return newRow;
    });

    console.log("Extracted data (3 hardcoded columns):", extractedData);
    return extractedData;
  };

  /* ================= CONVERT VALUE ================= */

  const convertValue = (value: any) => {
    if (value === null || value === undefined) return "";

    if (value instanceof Date) {
      return value.toISOString();
    }

    if (typeof value === "string") {
      const d = new Date(value);
      if (!isNaN(d.getTime())) {
        return d.toISOString();
      }
      return value;
    }

    if (typeof value === "number" || typeof value === "boolean") {
      return value;
    }

    return value;
  };

  /* ================= CONVERT TABLE TO JSON WITH RENAMED COLUMNS ================= */

  const convertTableToJson = (rows: Record<string, any>[]) => {
    const cleanedRows = rows.map((row) => {
      const newRow: Record<string, any> = {};

      // RENAME COLUMNS WHILE CONVERTING
      Object.keys(row).forEach((key) => {
        // Use mapped name if exists, otherwise keep original
        const renamedKey = COST_MODEL_COLUMN_MAPPING[key] || key;
        newRow[renamedKey] = convertValue(row[key]);
      });

      return newRow;
    });

    const finalJson = {
      data: {
        input_data: cleanedRows
      }
    };

    console.log("FINAL STRUCTURED JSON (with renamed columns):", finalJson);
    console.log("📝 Column names sent to SimplAI:", Object.keys(cleanedRows[0] || {}));
    return finalJson;
  };

  /* ================= HANDLE COST MODEL ANALYSIS ================= */

  const handleCostModelAnalysis = async () => {
    try {
      // VALIDATION: Check if excel data exists
      if (!excelData || excelData.length === 0) {
        toast.error("No excel data available. Please upload file in Data Ingestion step.");
        console.warn("⚠️ Excel data is empty");
        return;
      }

      console.log("🚀 Starting Cost Model Analysis...");
      console.log("📌 Column mapping:", COST_MODEL_COLUMN_MAPPING);

      // STEP 1: Extract 3 hardcoded columns
      const extractedData = extractThreeColumns(excelData);
      
      if (!extractedData) {
        toast.error("Failed to extract data from excel");
        console.error("❌ Extraction failed");
        return;
      }

      // STEP 2: Convert to JSON structure with RENAMED columns
      const jsonData = convertTableToJson(extractedData);

      // STEP 3: Call SimplAI with tool_id 52 (Cost Model Analysis)
      console.log("📤 Calling SimplAI Cost Model Analysis API...");
      const result = await runCostModelAnalysis(jsonData);

      console.log("Analysis Result:", result);

      // STEP 4: Validate result
      const valid = result?.data || result?.trace_id || result;

      if (!valid) {
        console.warn("⚠️ Invalid SimplAI response:", result);
        toast.error("Invalid SimplAI response");
        return;
      }

      // STEP 5: Store and display result
      setAnalysisResult(result);
      setContextAnalysisResult(result); // SAVE TO CONTEXT FOR COSTMODELANALYSISVIEW
      setShowResult(true);

      toast.success("Cost Model Analysis completed successfully!");
      console.log("Analysis completed and result stored");

      // STEP 6: Navigate to Cost Model Analysis view
      navigate("cost-model-analysis");

    } catch (err: any) {
      console.error("❌ Cost Model Analysis Error:", err);
      toast.error(err?.message || "Cost Model Analysis failed");
    }
  };

  // Get display names for UI
  const displayColumns = Object.entries(COST_MODEL_COLUMN_MAPPING)
    .map(([_, renamed]) => renamed)
    .join(", ");
  const products = groupCubeRows(rows, "Product_Name_Normalized").slice(0, 10);
  const productData = products.map((product, index) => ({
    label: product.label,
    value: product.amount / (products[0]?.amount || 1) * 100,
    color: productColors[index],
    amount: usd(product.amount),
  }));

  return (
    <div className="spendbyproduct-panel">
      {/* HEADER WITH BUTTON */}
      <div className="spendbyproduct-panel__header flex items-center justify-between">
        <div className="spendbyproduct-panel__title">SPEND BY PRODUCT</div>
        <div className="cost-model-analysis-btn-wrap">
          <button
            className="action-btn cost-model-analysis-btn"
            type="button"
            onClick={handleCostModelAnalysis}
            disabled={loading || excelData.length === 0}
            title={`Analyzing columns: ${displayColumns}`}
          >
            {loading ? (
              <>
                <span className="spinner" style={{ marginRight: "8px" }}>⏳</span>
                Analyzing...
              </>
            ) : (
              "Cost Model Analysis"
            )}
          </button>
        </div>
      </div>

      {/* LOADING STATE */}
      {loading && (
        <div className="ai-processing-overlay">
          <div className="loader_pulse">
            <div></div><div></div><div></div><div></div><div></div>
          </div>
          <div className="ai-processing-overlay__label">AI is analyzing cost model with columns: {displayColumns}</div>
        </div>
      )}

      {/* ANALYSIS RESULT DISPLAY */}
      {showResult && analysisResult && (
        <div className="analysis-result-panel" style={{
          marginBottom: "20px",
          padding: "16px",
          backgroundColor: "#F8F3FA",
          borderRadius: "8px",
          border: "1px solid #E0D4F0"
        }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "12px"
          }}>
            <div className="analysis-result__header" style={{ fontSize: "14px", fontWeight: "600" }}>
              Cost Model Analysis Result
            </div>
            <button
              onClick={() => setShowResult(false)}
              style={{
                background: "none",
                border: "none",
                fontSize: "18px",
                cursor: "pointer"
              }}
            >
              ✕
            </button>
          </div>

          {/* SHOW COLUMNS USED */}
          <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
            📊 Columns analyzed: {displayColumns}
          </div>

          {/* SHOW COLUMN MAPPING */}
          <div style={{ fontSize: "11px", color: "#999", marginBottom: "8px" }}>
            🔄 Mapping: {Object.entries(COST_MODEL_COLUMN_MAPPING)
              .map(([orig, renamed]) => `${orig}→${renamed}`)
              .join(" | ")}
          </div>

          {/* SHOW TRACE ID */}
          {analysisResult.trace_id && (
            <div style={{ fontSize: "12px", color: "#666", marginBottom: "8px" }}>
              📌 Trace ID: {analysisResult.trace_id}
            </div>
          )}

          {/* SHOW RESULT DATA */}
          <pre className="analysis-result__content" style={{
            backgroundColor: "#fff",
            padding: "12px",
            borderRadius: "4px",
            fontSize: "12px",
            maxHeight: "300px",
            overflow: "auto",
            border: "1px solid #E0D4F0"
          }}>
            {JSON.stringify(analysisResult.data || analysisResult, null, 2)}
          </pre>
        </div>
      )}

      {/* SPEND BY PRODUCT BARS (ORIGINAL) */}
      <div className="spendbyproduct-panel__bars">
        {productData.map((row) => (
          <div className="spendbyproduct-panel__row" key={row.label}>
            <div className="spendbyproduct-panel__label">{row.label}</div>
            <div className="spendbyproduct-panel__bar-wrap">
              <div
                className="spendbyproduct-panel__bar"
                style={{ width: `${row.value}%`, background: row.color }}
              />
            </div>
            <div className="spendbyproduct-panel__amount">{row.amount}</div>
          </div>
        ))}
      </div>
    </div>
  );
}